import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import React, { ReactElement, ReactNode, Children, forwardRef, useMemo } from 'react';
import FloatingOverlay from 'src/themes/cosmos/components/FloatingOverlay';
import IconButton from 'src/themes/cosmos/components/IconButton';
import { ArrowBackIcon, CloseIcon } from 'src/themes/cosmos/icons';
import useMenuRoute from '../../hooks/useMenuRoute';
import styles from './styles.module.scss';

interface ISwitchMenuProps {
  children: ReactNode;
  className?: string;
  onClose?: () => void;
}

const SwitchMenu = forwardRef<HTMLDivElement, ISwitchMenuProps>(({ children: elements, className, onClose }, ref) => {
  const { t } = useTranslation('common');
  const { goBack, currentMenuRoute } = useMenuRoute();

  const children = Children.toArray(elements) as ReactElement[];

  const itemMenuActiveIndex = useMemo(
    () =>
      Math.max(
        children.findIndex((item) => item.props.path === currentMenuRoute),
        0,
      ),
    [children, currentMenuRoute],
  );

  return (
    <FloatingOverlay
      ref={ref}
      className={classNames(styles.switchContainer, className)}
      title={children[itemMenuActiveIndex]?.props.title || ''}
      rightIcon={
        <IconButton
          data-testid='settings-close-button'
          aria-label={t('aria_labels.settings.close_button')}
          icon={<CloseIcon />}
          className={styles.closeButton}
          onClick={onClose}
        />
      }
      leftIcon={
        children[itemMenuActiveIndex]?.props.showBackButton && (
          <IconButton
            aria-label={t('aria_labels.settings.back_button')}
            icon={<ArrowBackIcon />}
            className={styles.backButton}
            onClick={() => goBack()}
          />
        )
      }
    >
      {children[itemMenuActiveIndex]}
    </FloatingOverlay>
  );
});

SwitchMenu.displayName = 'SwitchMenu';

export default SwitchMenu;
